import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  Article,
  B2bCheckoutService,
  B2bSellerService,
  B2bSessionService,
  ItemRecord,
  OrderRecord,
} from "b2b-ecommerce-lib";
import { catchError, EMPTY, firstValueFrom, map, of, switchMap, throwError } from "rxjs";
import { ToastService } from "../core/common-services/toast.service";
import { CatalogService } from "./catalog.service";

export type { CartItemRecord } from "b2b-ecommerce-lib";

@Injectable({
  providedIn: "root",
})
export class CartService extends B2bCheckoutService {
  catalogService = inject(CatalogService);
  sellersService = inject(B2bSellerService);
  toast = inject(ToastService);
  latestOrderDetails = signal<OrderRecord[]>([]);
  latestOrderCartItems = signal<ItemRecord[]>([]);

  // Cache for seller thresholds
  private _cachedSellerIds = signal<string[]>([]);
  private _cachedThresholds = signal<Map<string, number>>(new Map());

  isCartEmpty$ = toObservable(this.isCartEmpty);
  constructor(http: HttpClient, sessionService: B2bSessionService) {
    super(http, sessionService);
    sessionService.isAuthenticated$
      .pipe(switchMap((isAuthenticated) => (isAuthenticated ? this.isCartEmpty$ : EMPTY)))
      .subscribe((cartIsEmpty) => {
        if (!cartIsEmpty) this.getData();
      });
  }

  /**
   * Clear the thresholds cache manually
   */
  clearThresholdsCache(): void {
    this._cachedSellerIds.set([]);
    this._cachedThresholds.set(new Map());
  }

  // Se presente il prezzo scontato utilizza quello, altrimenti il prezzo normale
  private getEffectivePrice(item: ItemRecord): number {
    return item.discountedPrice ?? item.price ?? 0;
  }
  /**
   * Check if seller IDs have changed and update cache accordingly
   */
  private async getThresholds(sellerIds: string[]): Promise<Map<string, number>> {
    // STEP 1: Ordino gli ID e la cache per fare una comparazione
    const sortedSellerIds = [...sellerIds].sort();
    const sortedCachedIds = [...this._cachedSellerIds()].sort();

    // STEP 2: Controllo se ci sono stati cambi negli ID dei venditori + cache
    const isThresholdUnchanged =
      sortedSellerIds.length === sortedCachedIds.length &&
      sortedSellerIds.every((id, index) => id === sortedCachedIds[index]);

    // STEP 4: Se non ci sono stati cambi, restituisco i risultati precedenti della cache ed evito di fare chiamate inutili
    if (isThresholdUnchanged && this._cachedThresholds().size > 0) {
      // Return cached thresholds
      return this._cachedThresholds();
    }

    // STEP 4: Se ci sono stati cambi, recupero i nuovi valori delle soglie
    const thresholds = await firstValueFrom(
      this.sellersService.getSellerShippingThreshold(sellerIds),
    );

    // STEP 5: Aggiorno la cache e restituisco i valori
    const thresholdsMap = new Map<string, number>();
    sellerIds.forEach((id, index) => {
      const thresholdObj = thresholds[index];
      if (thresholdObj && thresholdObj.freeShippingThreshold != null) {
        // per non trattare un valore null come se fosse 0
        thresholdsMap.set(id, thresholdObj.freeShippingThreshold);
      } else {
        thresholdsMap.set(id, -1); // fallback, non mettere a 0 perchè se la soglia non c'è imposta spedizione gratuita
      }
    });

    this._cachedSellerIds.set(sellerIds);
    this._cachedThresholds.set(thresholdsMap);

    return thresholdsMap;
  }

  async getData() {
    if (this.cartArticles().length === 0) return;
    try {
      // estraggo gli itemId dal carrello
      const itemIds = this.cartArticles().map((p) => p?.itemId);
      const res = await firstValueFrom(this.catalogService.getItemsById(itemIds)); // da BE i dettagli dei prodotti tramite itemId

      const products = res.payload.content; //estrae i prodotti dalla risposta BE

      // AGGIORNAMENTO DATI CARRELLO
      const updatedCart = this.cartArticles().map((item) => {
        const product = products.find((p: ItemRecord) => p.uuid === item.itemId); //trova il prodotto corrispondente
        if (!product) return item;
        return {
          ...item, // prodotto del carrello
          ...product, // sovrascrive con le info aggiornate BE del prodotto
          quantity: item.quantity, // quantità del carrello
          maxQuantity: product?.quantity ?? item.quantity,
        };
      });

      // RECUPERO SOGLIE PER OGNI VENDITORE (con caching)
      const sellerIds = [
        ...new Set(updatedCart.map((p) => p.sellerId).filter((id): id is string => id != null)),
      ];
      const thresholdsMap = await this.getThresholds(sellerIds);

      // CALCOLO SPEDIZIONE GRATUITA
      const finalCart = updatedCart.map((item) => {
        const sellerId = item.sellerId;
        const sellerItems = updatedCart.filter((i) => i.sellerId === sellerId); //trova tutti i prodotti dello stesso venditore
        let totalForSeller = 0;
        for (const prod of sellerItems) {
          totalForSeller += this.getEffectivePrice(prod) * (prod.quantity ?? 0);
        } // somma totali per quel venditore (se c'è il discountedPrice usa quello)
        const threshold = thresholdsMap.get(sellerId ?? "") ?? 0; //prendo la soglia dalla mappa
        const isFreeShipping = threshold != null && threshold > 0 && totalForSeller >= threshold;
        //spedizione gratuita solo se la soglia esiste, è maggiore di 0 e il totale per quel venditore supera la soglia
        return {
          ...item,
          shippingCost: isFreeShipping ? 0 : (item.shippingCost ?? 0), //se ha diritto azzero i costi
        };
      });

      // RICALCOLO DEI TOTALI
      let totalShipping = 0;
      let totalPriceWithoutShipping = 0;
      //scorro il carrello
      for (const item of finalCart) {
        const quantity = item.quantity ?? 0;
        // la spedizione e' per singola copia, quindi va moltiplicata per la quantita'
        totalShipping += (item.shippingCost ?? 0) * quantity;
        totalPriceWithoutShipping += this.getEffectivePrice(item) * quantity;
        // somma prezzi x quantità (se c'è il discountedPrice usa quello)
      }
      const totalPrice = totalPriceWithoutShipping + totalShipping;

      // AGGIORNO I SIGNAL x IL TEMPLATE
      this._totalPriceWithoutShipping.set(totalPriceWithoutShipping);
      this._totalShipping.set(totalShipping);
      this._totalPrice.set(totalPrice);
      this.cartArticles.set(finalCart);
    } catch (err: unknown) {
      console.error(err);
      if (err instanceof Error && err.message === "204") {
        this.toast.showInfoToastByKey("toast.cart.empty");
      } else {
        this.toast.showErrorToastByKey("toast.cart.retrieveError");
      }
    }
  }

  async reloadCart() {
    const articles = await firstValueFrom(
      this.loadCurrentCartArticles().pipe(
        map((res) => res.payload),
        catchError((err) => {
          if (err.status === 404) return of([]);
          return throwError(() => err);
        }),
      ),
    );
    this.cartArticles.update(() => articles);
    this.clearThresholdsCache();

    await this.getData();
  }

  /**
   * Forza la sincronizzazione del carrello dal server
   * Utile quando si sa che il server ha dati più aggiornati del client
   */
  async forceSyncFromServer() {
    try {
      // Resetta lo stato locale
      this.cartArticles.set([]);

      // Clear thresholds cache
      this._cachedSellerIds.set([]);
      this._cachedThresholds.set(new Map());

      // Ricarica dal server
      await this.reloadCart();
    } catch (error) {
      console.error("Errore nella sincronizzazione forzata:", error);
    }
  }

  override addArticleToCart(newArticle: Article): void {
    super.addArticleToCart(newArticle);
    // Clear cache when adding new article since it might introduce new sellers
    this._cachedSellerIds.set([]);
    this._cachedThresholds.set(new Map());
    this.getData();
  }

  private _totalShipping = signal<number>(0);
  private _totalPriceWithoutShipping = signal<number>(0);
  private _totalPrice = signal<number>(0);

  // Override getter del B2bCheckoutService necessario per avere i valori aggiornati
  override get totalShipping() {
    return this._totalShipping();
  }

  override get totalPriceWithoutShipping() {
    return this._totalPriceWithoutShipping();
  }

  override get totalPrice() {
    return this._totalPrice();
  }
}
